import {Button, MenuItem} from '@material-ui/core';
import {
  Async,
  AsyncError,
  Conflict,
  createLoading,
  fromResult,
  isLoaded,
  isLoading,
  matchAsync,
  Pending,
  ServerError,
  toEither,
  Unauthorized,
} from 'Core/models/Async';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {matchPassword, toRequirement, ValidPassword} from 'Core/models/Password';
import {RoleId} from 'Core/models/Role';
import {
  InvalidSecurityQuestion,
  matchSecurityQuestion,
  UnvalidatedSecurityQuestion,
  ValidSecurityQuestion,
} from 'Core/models/SecurityQuestion';
import {sequenceT} from 'fp-ts/lib/Apply';
import * as E from 'fp-ts/lib/Either';
import {either, fold} from 'fp-ts/lib/Either';
import {flow} from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import Alert from 'Shared/components/Alert';
import CardField from 'Shared/components/CardField';
import ErrorPage from 'Shared/components/ErrorPage';
import {Errors} from 'Shared/components/ErrorPopover';
import {default as Loading, default as LoadingSpinner} from 'Shared/components/Loading';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import ValidatableEmailInput from 'Shared/components/ValidatableEmailInput';
import ValidatablePhoneInput from 'Shared/components/ValidatablePhoneInput';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import ValidatableTextInput from 'Shared/components/ValidatableTextInput';
import {notEq} from 'Shared/utilities/equals';
import * as IndependentAgentActions from 'Users/ducks/independentAgent';
import * as RegistrationActions from 'Users/ducks/registration';
import {
  SavingUserRegistration,
  UserRegistration,
  UserRegistrationWithError,
  ValidUserRegistration,
} from 'Users/models/UserRegistration';
import {UserRouteConstants} from 'Users/Routes';
import './ExternalUserSignUp.sass';
import './Form.sass';

type SignUpFormProps = {
  getSecurityQuestions: typeof IndependentAgentActions.getSecurityQuestions;
  getPreregisteredUser: typeof IndependentAgentActions.getPreregisteredUser;
} & ReturnType<typeof mapStateToProps> &
  typeof RegistrationActions &
  RouteComponentProps<{preregistrationKey: string}>;

const UserRegistrationLens = Lens.fromProp<UserRegistration>();
const getRoleFromId = (role: any) => {
  return role.id === RoleId.Producer ? 'Producer' : 'Support Agent' ? role?.name : 'Unknown';
};
const getUserTypeText = (userType: any) => {
  const agentRoles = userType.agencyAssociations ? userType.agencyAssociations[0]?.roles : [];
  const enrollmentTrackingRoles = userType?.roles || [];
  const allRoles = [...agentRoles, ...enrollmentTrackingRoles];

  return allRoles.map(getRoleFromId).join(', ');
};

const getRoleText = (user: any) => user.userTypes.map(getUserTypeText).join(', ');

const ExternalUserSignUp = (props: SignUpFormProps) => {
  const preregistrationKey = props.match.params.preregistrationKey;
  const getUserKey = () => O.fromNullable(preregistrationKey);

  useEffect(() => {
    props.getSecurityQuestions();

    if (preregistrationKey) {
      props.getPreregisteredUser(preregistrationKey);
    }
  }, []);

  const returnToSignUp = () => {
    props.history.push(UserRouteConstants.EXTERNAL_USER_REGISTRATION(preregistrationKey));
  };

  const [hasBlurredPassword, setHasBlurredPassword] = useState(false);
  const [hasBlurredSecurityQuestionOne, setHasBlurredSecurityQuestionOne] = useState(false);
  const [hasBlurredSecurityQuestionTwo, setHasBlurredSecurityQuestionTwo] = useState(false);

  const loadedData = sequenceT(either)(toEither(props.securityQuestions), toEither(props.preregisteredUser));
  const mappedUsername = matchAsync(props.registration.userName, {
    Loaded: value =>
      pipe(
        value,
        E.map(value => NonEmptyString.create(value.value) as NonEmptyString),
        fromResult
      ),
    Loading: value =>
      isLoading(value)
        ? pipe(
            value.Data,
            O.fromNullable,
            O.map(E.map(value => NonEmptyString.create(value.value) as NonEmptyString)),
            O.getOrElse(() => E.left(new EmptyString() as StringUnion)),
            createLoading
          )
        : value,
    Conflict: () => new Conflict() as Async<E.Either<StringUnion, NonEmptyString>>,
    ServerError: value => new ServerError(value),
    Unauthorized: () => new Unauthorized(),
  });

  return pipe(
    loadedData,
    fold(handleError, asyncData => {
      const [questions, preregisteredUser] = asyncData;

      if (props.registration instanceof SavingUserRegistration) {
        return <Loading />;
      }

      return (
        <div className="external-user-sign-up">
          <aside className="external-user-sign-up__welcome-background" />
          <form className="external-user-form" autoComplete="off">
            <div className="sign-up-form">
              {props.registration instanceof UserRegistrationWithError && (
                <Alert message={props.registration.errorMessage} />
              )}
              <h1 className="external-user-sign-up__new-account-title">New Account</h1>
              <div className="external-user-form__preregistration-details">
                <CardField
                  name="Name"
                  className="external-user-form__preregistration-field"
                  value={`${preregisteredUser.lastName}, ${preregisteredUser.firstName}`}
                />
                <CardField
                  name="Role"
                  className="external-user-form__preregistration-field"
                  value={getRoleText(preregisteredUser)}
                />
              </div>
              <p>Please provide the following information to complete your registration:</p>
              <ValidatableEmailInput
                className="external-user-form__field"
                label="Email"
                value={props.registration.email}
                onValidate={email =>
                  pipe(getUserKey, IO.map(O.map(registrationKey => props.validateEmail(email, registrationKey))))()
                }
              />
              <ValidatableTextInput
                label="Username"
                onValidate={username => {
                  const removeUnusableCharacters = (value: string) => value.replace(/\#|\!|\s/g, '');
                  const newUserNameToValidate = removeUnusableCharacters(username);

                  pipe(
                    getUserKey,
                    IO.map(O.map(registrationKey => props.validateUsername(newUserNameToValidate, registrationKey)))
                  )();
                }}
                additionalErrors={
                  isLoaded(props.registration.userName) &&
                  E.isRight(props.registration.userName.Data) &&
                  props.registration.userName.Data.right instanceof NonEmptyString
                    ? ['Username must be greater than 10 characters.']
                    : []
                }
                value={mappedUsername}
              />
              <span className="external-user-form__username-footnote">
                * Username cannot contain spaces or the following symbols: !, #.
              </span>
              <ValidatablePhoneInput
                label="Mobile Phone"
                className="external-user-form__field"
                value={props.registration.phoneNumber}
                onValidate={phone =>
                  pipe(
                    getUserKey,
                    IO.map(O.map(registrationKey => props.validatePhoneNumber(phone, registrationKey)))
                  )()
                }
              />
              <ValidatableTextField
                className="external-user-form__field"
                label="Password"
                showErrors={errors =>
                  hasBlurredPassword && Errors.is.requirements(errors) && errors.value.some(error => !error.isMet)
                }
                errors={Errors.requirements({
                  value: matchPassword(props.registration.password, {
                    InvalidPassword: invalid => toRequirement(invalid),
                    UnvalidatedPassword: unvalidated => toRequirement(unvalidated),
                    ValidPassword: valid => toRequirement(valid),
                  }),
                })}
                inputProps={{
                  type: 'password',
                }}
                onChange={value =>
                  props.changeUserRegistration(
                    UserRegistrationLens('password').set(
                      ValidPassword.create(value, props.registration.password.passwordLine2)
                    )(props.registration)
                  )
                }
                value={props.registration.password.passwordLine1}
              />
              <ValidatableTextField
                className="external-user-form__field"
                label="Confirm Password"
                showErrors={errors =>
                  hasBlurredPassword && Errors.is.requirements(errors) && errors.value.some(error => !error.isMet)
                }
                errors={Errors.requirements({
                  value: matchPassword(props.registration.password, {
                    InvalidPassword: invalid => toRequirement(invalid),
                    UnvalidatedPassword: unvalidated => toRequirement(unvalidated),
                    ValidPassword: valid => toRequirement(valid),
                  }),
                })}
                inputProps={{
                  type: 'password',
                }}
                onChange={value =>
                  props.changeUserRegistration(
                    UserRegistrationLens('password').set(
                      ValidPassword.create(props.registration.password.passwordLine1, value)
                    )(props.registration)
                  )
                }
                onBlur={() => setHasBlurredPassword(true)}
                value={props.registration.password.passwordLine2}
              />
              <OutlinedSelect
                label="Security Question One"
                className="external-user-form__field"
                inputProps={{
                  id: 'security-question-one',
                  name: 'security-question-one',
                }}
                value={props.registration.securityQuestionOne.question}
                onChange={event => {
                  props.changeUserRegistration(
                    UserRegistrationLens('securityQuestionOne').set(
                      new UnvalidatedSecurityQuestion(event.target.value, props.registration.securityQuestionOne.answer)
                    )(props.registration)
                  );
                }}
                error={props.registration.securityQuestionOne instanceof InvalidSecurityQuestion}
              >
                {questions.filter(notEq('id', props.registration.securityQuestionTwo.question)).map(question => (
                  <MenuItem value={question.id}>{question.question}</MenuItem>
                ))}
              </OutlinedSelect>
              <ValidatableTextField
                className="external-user-form__field"
                value={props.registration.securityQuestionOne.answer}
                label="Security Answer 1"
                errors={Errors.standardErrors({
                  value: matchSecurityQuestion(props.registration.securityQuestionOne, {
                    Invalid: invalidQuestion => invalidQuestion.errors,
                    Unvalidated: () => [],
                    Valid: () => [],
                  }),
                })}
                showErrors={errors => hasBlurredSecurityQuestionOne && errors.value.length > 0}
                onChange={answer =>
                  props.changeUserRegistration(
                    UserRegistrationLens('securityQuestionOne').set(
                      ValidSecurityQuestion.create(props.registration.securityQuestionOne.question, answer)
                    )(props.registration)
                  )
                }
                onBlur={() => setHasBlurredSecurityQuestionOne(true)}
              />
              <OutlinedSelect
                label="Security Question Two"
                className="external-user-form__field"
                inputProps={{
                  id: 'security-question-two',
                  name: 'security-question-two',
                }}
                value={props.registration.securityQuestionTwo.question || []}
                onChange={event => {
                  props.changeUserRegistration(
                    UserRegistrationLens('securityQuestionTwo').set(
                      new UnvalidatedSecurityQuestion(event.target.value, props.registration.securityQuestionTwo.answer)
                    )(props.registration)
                  );
                }}
                error={props.registration.securityQuestionOne instanceof InvalidSecurityQuestion}
              >
                {questions.filter(notEq('id', props.registration.securityQuestionOne.question)).map(question => (
                  <MenuItem value={question.id}>{question.question}</MenuItem>
                ))}
              </OutlinedSelect>
              <ValidatableTextField
                value={props.registration.securityQuestionTwo.answer}
                className="external-user-form__field"
                label="Security Answer 2"
                errors={Errors.standardErrors({
                  value: matchSecurityQuestion(props.registration.securityQuestionTwo, {
                    Invalid: invalidQuestion => invalidQuestion.errors,
                    Unvalidated: () => [],
                    Valid: () => [],
                  }),
                })}
                onChange={answer =>
                  props.changeUserRegistration(
                    UserRegistrationLens('securityQuestionTwo').set(
                      ValidSecurityQuestion.create(props.registration.securityQuestionTwo.question, answer)
                    )(props.registration)
                  )
                }
                showErrors={errors => hasBlurredSecurityQuestionTwo && errors.value.length > 0}
                onBlur={() => setHasBlurredSecurityQuestionTwo(true)}
              />
            </div>
            <div className="sign-up-actions">
              <Button
                id="return-button"
                className="sign-up-actions__return"
                variant="contained"
                color="inherit"
                onClick={returnToSignUp}
              >
                Cancel
              </Button>
              <Button
                id="registration-button"
                disabled={!(props.registration instanceof ValidUserRegistration)}
                className="sign-up-actions__register"
                variant="contained"
                type="submit"
                color="primary"
                onClick={() => {
                  pipe(
                    getUserKey,
                    IO.map(
                      flow(
                        O.fold(
                          () => {},
                          userKey => {
                            if (props.registration instanceof ValidUserRegistration) {
                              props.registerUser(
                                preregisteredUser.firstName,
                                preregisteredUser.lastName,
                                userKey,
                                props.registration
                              );
                            }
                          }
                        )
                      )
                    )
                  )();
                }}
              >
                Register
              </Button>
            </div>
          </form>
        </div>
      );
    })
  );
};

export const handleError = (asyncError: Pending | AsyncError) => {
  switch (asyncError.key) {
    case 'Empty':
    case 'Loading':
      return <LoadingSpinner />;
    case 'ServerError':
    case 'NotFound':
    case 'Unauthorized':
    case 'Conflict':
      return (
        <ErrorPage
          error={{
            message: 'The registration link is either invalid or expired. Please contact your administrator.',
          }}
        />
      );
  }
};

const mapStateToProps = (state: ReducerState) => ({
  registration: state.user.registration.userRegistration,
  preregisteredUser: state.user.independentAgent.preregisteredUser,
  securityQuestions: state.user.independentAgent.securityQuestions,
});

export default withRouter(
  connect(mapStateToProps, {
    ...RegistrationActions,
    getSecurityQuestions: IndependentAgentActions.getSecurityQuestions,
    getPreregisteredUser: IndependentAgentActions.getPreregisteredUser,
  })(ExternalUserSignUp)
);
